"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"

type ProgressState = "idle" | "loading" | "completing"

export function NavigationProgress() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [state, setState] = useState<ProgressState>("idle")
  const [width, setWidth] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevRouteRef = useRef(`${pathname}?${searchParams}`)

  useEffect(() => {
    const currentRoute = `${pathname}?${searchParams}`
    if (currentRoute === prevRouteRef.current) return
    prevRouteRef.current = currentRoute

    // Navigation completed — snap to 100% then fade out
    if (state === "loading") {
      setWidth(100)
      setState("completing")
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setState("idle")
        setWidth(0)
      }, 400)
    }
  }, [pathname, searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // Detect navigation start via click on <a> tags
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const anchor = (e.target as Element).closest("a")
      if (!anchor) return
      const href = anchor.getAttribute("href")
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return
      if (anchor.target === "_blank") return

      // Same-page hash navigation — skip
      const url = new URL(href, window.location.href)
      if (url.origin !== window.location.origin) return
      const currentRoute = `${pathname}?${searchParams}`
      const nextRoute = `${url.pathname}?${url.searchParams}`
      if (nextRoute === currentRoute) return

      // Start progress
      if (timerRef.current) clearTimeout(timerRef.current)
      setState("loading")
      setWidth(0)

      // Ramp quickly to ~80% via requestAnimationFrame trick
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setWidth(80)
        })
      })
    }

    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [pathname, searchParams])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  if (state === "idle") return null

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${width}%`,
          backgroundColor: "var(--primary)",
          transition:
            state === "loading"
              ? "width 800ms cubic-bezier(0.1, 0.4, 0.3, 1)"
              : "width 150ms ease-out",
          opacity: state === "completing" ? 0 : 1,
          transitionProperty: state === "completing" ? "width, opacity" : "width",
          transitionDuration: state === "completing" ? "150ms, 300ms" : "800ms",
          transitionTimingFunction:
            state === "completing" ? "ease-out, ease-in" : "cubic-bezier(0.1, 0.4, 0.3, 1)",
          transitionDelay: state === "completing" ? "0ms, 150ms" : "0ms",
        }}
      />
    </div>
  )
}
